import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
  uploadFileToStaging,
  createDriveReport,
  DriveReportMetadata,
  DriveUploadResponse,
} from '@/integrations/supabase/driveUploadService';

export interface DriveUploadInput {
  file: File;
  metadata: DriveReportMetadata;
}

export interface UseDriveUploadReturn {
  uploadReport: (input: DriveUploadInput) => Promise<DriveUploadResponse>;
  isUploading: boolean;
  progress: number;
  stage: 'idle' | 'staging' | 'processing' | 'done';
  error: string | null;
  reset: () => void;
}

export function useDriveUpload(): UseDriveUploadReturn {
  const [progress, setProgress] = useState(0);
  const [stage, setStage] = useState<'idle' | 'staging' | 'processing' | 'done'>('idle');
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async ({ file, metadata }: DriveUploadInput): Promise<DriveUploadResponse> => {
      setError(null);
      setProgress(0);
      setStage('staging');

      // Phase 1: Upload to Supabase Storage staging (0→65%)
      const progressInterval = setInterval(() => {
        setProgress(prev => (prev < 60 ? prev + 5 : prev));
      }, 300);

      let storagePath: string;
      try {
        storagePath = await uploadFileToStaging(file);
      } finally {
        clearInterval(progressInterval);
      }

      setProgress(65);
      setStage('processing');

      // Phase 2: Edge Function processes + uploads to Drive (65→95%)
      const edgeInterval = setInterval(() => {
        setProgress(prev => (prev < 93 ? prev + 2 : prev));
      }, 500);

      let result: DriveUploadResponse;
      try {
        result = await createDriveReport(storagePath, file.name, file.type, metadata);
      } finally {
        clearInterval(edgeInterval);
      }

      if (!result.success) {
        throw new Error(result.error || 'Upload failed');
      }

      setProgress(100);
      setStage('done');
      return result;
    },
    onSuccess: async (data) => {
      toast({
        title: 'Tạo báo cáo thành công',
        description: 'File đã được lưu lên Google Drive.',
      });

      // Refetch immediately and await completion so the list updates
      // before the dialog closes (1800ms timeout in CreateReportDialog)
      await queryClient.refetchQueries({ queryKey: ['supabase-reports'] });

      // Invalidate related caches in background (no need to await)
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      queryClient.invalidateQueries({ queryKey: ['supabase-period-navigation'] });
      queryClient.invalidateQueries({ queryKey: ['reports-stats'] });

      setTimeout(() => {
        setProgress(0);
        setStage('idle');
      }, 2000);
    },
    onError: (err: Error) => {
      setError(err.message);
      setProgress(0);
      setStage('idle');

      toast({
        title: 'Tạo báo cáo thất bại',
        description: err.message,
        variant: 'destructive',
      });
    },
  });

  const reset = () => {
    setProgress(0);
    setStage('idle');
    setError(null);
    mutation.reset();
  };

  return {
    uploadReport: mutation.mutateAsync,
    isUploading: mutation.isPending,
    progress,
    stage,
    error,
    reset,
  };
}
